import { Flex, Typography } from "antd";
import { TxButton } from "ui";
import {
  dateFormat,
  EXPLORER_LINK_TYPE,
  formatWithDecimals,
  getBlockchainExplorerLink,
  getNetworkDecimalPlaces,
  getShortName,
  isStatusIncluded,
} from "utils";
import { EditFeeButton } from "../EditFee";
import {
  BID_REQUEST_STATUS_ENUM,
  BidEntity,
  COMPANY_ROLE_ENUM,
  ItemInfoType,
  MODE_TYPE,
  NETWORK_TEXT,
  RequestProcessingHistoryType,
  SUB_STEP_STATUSES_ENUM,
} from "interfaces";
import { UserStore } from "stores/UserStore";
import { ADDITIONAL_FEES, DIRECT_PAYMENT_CUSTOMER_LP } from "utils/envConstants";
import { IStores } from "stores";
import { FeeTooltip } from "./FeeTooltip";
import { EditCustomFeeBtn } from "../EditCustomFee";

const getTooltipTxButton = (
  processingHistoryTooltip: Record<string, RequestProcessingHistoryType[]>,
  type: SUB_STEP_STATUSES_ENUM
) => {
  if (!processingHistoryTooltip[type]) {
    return;
  }
  return `${dateFormat(processingHistoryTooltip[type]?.[0]?.actionDate, true)} ${getShortName(
    processingHistoryTooltip[type]?.[0]?.surname ?? "",
    processingHistoryTooltip[type]?.[0]?.name ?? "",
    processingHistoryTooltip[type]?.[0]?.secondName ?? ""
  )}`;
};

const hasRubFee = (value: number | undefined): value is number => value !== undefined && value !== null;
const hasPercentFee = (value: number | undefined): value is number =>
  value !== undefined && value !== null && value >= 0;

const getRubFeeColumn = (show: boolean, label: string, value: number | undefined): ItemInfoType | false =>
  show &&
  hasRubFee(value) && {
    label,
    text: <Typography.Text className="text-middle">{formatWithDecimals(value)} RUB</Typography.Text>,
  };

export const getColumns = (
  data: BidEntity,
  user: UserStore,
  processingHistoryTooltip: IStores["bidHistory"]["processingHistoryTooltip"]
): Array<ItemInfoType> => {
  const {
    fiatAmount,
    fiatCurrency,
    currency,
    contract,

    quote,
    customerFee,
    lpFee,
    destinationWallet,
    network,
    status,
    splitCount,
    networkFee,
    feeDetails,
    amountRub,
    customerFeeRub,
    lpFeeRub,
  } = data;

  const { isOperator, isCustomer, isProvider, isSeniorOperator, isLPRiskManager, isJuniorOperator, isRiskManager } =
    user.roles;
  const isProviderOrLPRiskManager = isProvider || isLPRiskManager;

  const isExportContract = contract.direction === "EXP";
  const isImportContract = contract.direction === "IMP";

  const canEditFee =
    isStatusIncluded(status, [
      BID_REQUEST_STATUS_ENUM.DRAFT,
      BID_REQUEST_STATUS_ENUM.NEW,
      BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL,
      BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL_ACCEPTED,
      BID_REQUEST_STATUS_ENUM.DRAFT_APPROVED,
    ]) && !ADDITIONAL_FEES;

  const canEditCustomFee =
    isStatusIncluded(status, [
      BID_REQUEST_STATUS_ENUM.DRAFT,
      BID_REQUEST_STATUS_ENUM.NEW,
      BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL,
      BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL_ACCEPTED,
      BID_REQUEST_STATUS_ENUM.DRAFT_APPROVED,
    ]) && ADDITIONAL_FEES;

  const showDestinationWallet =
    network &&
    destinationWallet &&
    (isOperator || (isProviderOrLPRiskManager && isExportContract) || (isCustomer && isImportContract));

  const showNetworkFee = networkFee && network && (isProviderOrLPRiskManager || isOperator);

  const tooltipTxButtonIsExp =
    data.contract.direction === "EXP" &&
    (isProviderOrLPRiskManager || isOperator) &&
    getTooltipTxButton(processingHistoryTooltip, SUB_STEP_STATUSES_ENUM.VERIFY_WALLET_QUOTE);

  const tooltipTxButtonIsImp =
    data.contract.direction === "EXP" &&
    (isCustomer || isOperator) &&
    getTooltipTxButton(processingHistoryTooltip, SUB_STEP_STATUSES_ENUM.VERIFY_WALLET_REQUEST);

  const showFeeForOperator = isSeniorOperator || isJuniorOperator || isRiskManager;
  const showFeeForCustomer = isCustomer && (!canEditFee || hasPercentFee(customerFee));
  const showFeeForProvider = isProviderOrLPRiskManager && (!canEditFee || hasPercentFee(lpFee));
  // const isLpProvider = isJuniorLP || isSeniorLP;

  // const showRubBalance =
  //   rubBalance &&
  //   !DIRECT_PAYMENT_CUSTOMER_LP &&
  //   !isStatusIncluded(status, [
  //     BID_REQUEST_STATUS_ENUM.DRAFT,
  //     BID_REQUEST_STATUS_ENUM.DRAFT_APPROVED,
  //     BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL,
  //     BID_REQUEST_STATUS_ENUM.AGENT_PROPOSAL_ACCEPTED,
  //     BID_REQUEST_STATUS_ENUM.NEW,
  //     BID_REQUEST_STATUS_ENUM.QUOTE_PROPOSED,
  //   ]);
  // const showRubBalanceExp =
  //   isOperator ||
  //   (isCustomer && isExportContract && !requestProcessing.fiatReceived) ||
  //   (isLpProvider && isExportContract && requestProcessing.fiatReceived);
  // const showRubBalanceImp =
  //   isOperator ||
  //   (isCustomer && isImportContract && requestProcessing.fiatReceived) ||
  //   (isLpProvider && isImportContract && !requestProcessing.fiatReceived);

  return [
    quote && {
      label: `Курс 1 ${currency}`,
      text: `${formatWithDecimals(quote.rate)} RUB`,
    },
    {
      label: "Сумма в валюте договора",
      text: (
        <Typography.Text className="text-middle">
          {formatWithDecimals(fiatAmount)} {fiatCurrency}
        </Typography.Text>
      ),
    },
    showDestinationWallet && {
      label: (
        <>
          <> Кошелек получателя</> <br />
          <>{network && `в сети ${NETWORK_TEXT[network]}`}</>
        </>
      ),
      text: (
        <TxButton
          isWalletAddress
          text={destinationWallet || ""}
          href={getBlockchainExplorerLink(network, destinationWallet || "", EXPLORER_LINK_TYPE.address)}
          infoBlock
          showCopyBtn={false}
          truncateResponse={false}
          tooltipTxButton={tooltipTxButtonIsExp || tooltipTxButtonIsImp}
        />
      ),
    },
    // showRubBalance &&
    //   (showRubBalanceExp || showRubBalanceImp) && {
    //     label: "Баланс фиат",
    //     text: <Typography.Text className="text-middle">{formatWithDecimals(rubBalance)} RUB</Typography.Text>,
    //   },
    // showPlatformFeeToNonProvider && {
    //   label: "Комиссия платформы",
    //   text: quote ? `${formatWithDecimals(feeRub)} RUB` : `${formatWithDecimals(companyFee * 100, 2)} %`,
    // },
    isProviderOrLPRiskManager &&
      network &&
      isStatusIncluded(status, [BID_REQUEST_STATUS_ENUM.NEW]) && {
        label: "Сеть",
        text: `${NETWORK_TEXT[network]}`,
      },

    showFeeForOperator && {
      label: "Комиссия УВЭД",
      text: (
        <FeeTooltip
          value={
            canEditFee && (isSeniorOperator || isJuniorOperator) ? (
              <EditFeeButton text={`${formatWithDecimals(customerFee * 100, 2)} %`} />
            ) : canEditCustomFee && (isSeniorOperator || isJuniorOperator) ? (
              <EditCustomFeeBtn
                text={`${formatWithDecimals((customerFee || 0) * 100, 2)} %`}
                mode={MODE_TYPE.CUSTOMER}
              />
            ) : (
              <Typography.Text className="text-middle">{formatWithDecimals(customerFee * 100, 2)} %</Typography.Text>
            )
          }
          feeDetails={feeDetails}
          role={COMPANY_ROLE_ENUM.CUSTOMER}
          status={status}
          amountRub={amountRub}
          totalFee={customerFee}
          totalFeeRub={customerFeeRub}
          title="Комиссия УВЭД"
          fee={customerFee}
        />
      ),
    },

    getRubFeeColumn(showFeeForOperator, "Комиссия УВЭД в рублях", customerFeeRub),
    showFeeForOperator && {
      label: "Комиссия ПЛ",
      text: (
        <FeeTooltip
          value={
            canEditFee && (isSeniorOperator || isJuniorOperator) ? (
              <EditFeeButton text={`${formatWithDecimals(lpFee * 100, 2)} %`} />
            ) : canEditCustomFee && (isSeniorOperator || isJuniorOperator) ? (
              <EditCustomFeeBtn
                text={`${formatWithDecimals((lpFee || 0) * 100, 2)} %`}
                mode={MODE_TYPE.LIQUIDITY_PROVIDER}
              />
            ) : (
              <Typography.Text className="text-middle">{formatWithDecimals(lpFee * 100, 2)} %</Typography.Text>
            )
          }
          feeDetails={feeDetails}
          role={COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER}
          status={status}
          amountRub={amountRub}
          totalFee={lpFee}
          totalFeeRub={lpFeeRub}
          title="Комиссия ПЛ"
          fee={lpFee}
        />
      ),
    },

    getRubFeeColumn(showFeeForOperator, "Комиссия ПЛ в рублях", lpFeeRub),

    showFeeForCustomer && {
      label: "Комиссия",
      text: (
        <Flex>
          <FeeTooltip
            value={
              <Typography.Text className="text-middle">{formatWithDecimals(customerFee * 100, 2)} %</Typography.Text>
            }
            feeDetails={feeDetails}
            role={COMPANY_ROLE_ENUM.CUSTOMER}
            status={status}
            amountRub={amountRub}
            totalFee={customerFee}
            totalFeeRub={customerFeeRub}
            title="Комиссия"
            fee={customerFee}
          />
        </Flex>
      ),
    },
    getRubFeeColumn(showFeeForCustomer, "Комиссия в рублях", customerFeeRub),
    showFeeForProvider && {
      label: "Комиссия",
      text: (
        <Flex>
          <FeeTooltip
            value={<Typography.Text className="text-middle">{formatWithDecimals(lpFee * 100, 2)} %</Typography.Text>}
            feeDetails={feeDetails}
            role={COMPANY_ROLE_ENUM.LIQUIDITY_PROVIDER}
            status={status}
            amountRub={amountRub}
            totalFee={lpFee}
            totalFeeRub={lpFeeRub}
            title="Комиссия"
            fee={lpFee}
          />
        </Flex>
      ),
    },
    getRubFeeColumn(showFeeForProvider, "Комиссия в рублях", lpFeeRub),

    showNetworkFee && {
      label: "Комиссия блокчейна",
      text: (
        // <Tooltip
        //   title={
        //     splitCount > 1
        //       ? `Сумма рекомендованная, средств должно хватить на ${
        //           splitCount + 1
        //         } исходящих транзакций, остаток будет возвращен`
        //       : undefined
        //   }
        // >
        <Typography.Text className="text-middle">
          {formatWithDecimals(splitCount * networkFee, getNetworkDecimalPlaces(network))} {network}
        </Typography.Text>
        // </Tooltip>
      ),
    },
  ].filter((c) => !!c) as ItemInfoType[];
};

export const getTitle = (data: BidEntity): string | null => {
  const { status } = data;
  if (status === BID_REQUEST_STATUS_ENUM.CANCELED) {
    return "Заявка отменена";
  } else {
    return null;
  }
};
